<script lang="ts">
    import {
        VpCheckIcon,
        VpCloseIcon,
        VpReturnIcon,
        VpSearchIcon,
    } from '@decathlon-experience/icons/svelte';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import type { Option, OptionList } from '$saga/types/search.types';
    import { filterList } from '$saga/utils/searchFilters';
    import CheckboxElement from '$saga/views/Search/components/CheckboxElement.svelte';
    import FilterBadges from '$saga/views/Search/components/filterBadges/FilterBadges.svelte';
    import {
        arePillarFiltersActive,
        areSecondaryFiltersActive,
        filtersContext,
    } from '$saga/views/Search/store';
    import {
        getFilterChangeHandler,
        redirectToGenericSearch,
        shouldRedirectOnRemoval,
    } from '$saga/views/Search/utils';

    import NoResultsFilter from './NoResultsFilter.svelte';

    const debouncedHandleFilterChange = getFilterChangeHandler();

    export let defaultList: OptionList;
    export let displayedList: OptionList = [];
    export let handleValidateSelection: () => void;
    export let values: string[];
    export let labels: string[];
    export let placeholder: string;
    export let isSuggestion = false;
    export let allLabel: string | null = null;
    export let enableAlphabeticalGrouping = false;
    export let filterType: 'destination' | 'sport';

    $: baseList = displayedList.length > 0 ? displayedList : defaultList;
    let searchQuery = '';
    $: filteredOptions = searchQuery
        ? filterList({
              query: searchQuery,
              fullList: defaultList,
              displayedList,
          })
        : baseList;
    let isUserSearching = false;
    let inputElement: HTMLInputElement;
    let scrollContainerRef: HTMLDivElement;
    // local values — kept in sync with external store mutations (badge removal, reset, etc.)
    let selectedValue: string[] = values;
    let selectedLabel: string[] = labels;
    $: selectedValue = values;
    $: selectedLabel = labels;

    // Group options by their starting letter
    $: groupedOptions = (() => {
        const groups: { [key: string]: OptionList } = {};
        const sortedOptions = [...filteredOptions].sort((a, b) =>
            a.label.localeCompare(b.label),
        );

        sortedOptions.forEach((option) => {
            const firstLetter = option.label[0]?.toUpperCase() || '#';
            if (!groups[firstLetter]) {
                groups[firstLetter] = [];
            }
            groups[firstLetter].push(option);
        });

        return groups;
    })();

    $: availableLetters = Object.keys(groupedOptions).sort();
    const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    const scrollToLetter = (letter: string) => {
        const element = document.getElementById(`letter-group-${letter}`);
        if (element && scrollContainerRef) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const handleSelection = (option: Option) => {
        if (
            selectedValue.find((val) => val === 'all') &&
            option.value !== 'all'
        ) {
            selectedValue = [];
            selectedLabel = [];
        }

        if (selectedValue.includes(option.value)) {
            selectedValue = selectedValue.filter(
                (value) => value !== option.value,
            );
            selectedLabel = selectedLabel.filter(
                (label) => label !== option.label,
            );
        } else {
            selectedValue = [...selectedValue, option.value];
            selectedLabel = [...selectedLabel, option.label];
        }
        values = selectedValue;
        labels = selectedLabel;

        if (shouldRedirectOnRemoval(filterType, String(option.value))) {
            redirectToGenericSearch($filtersContext.formValues);
            return;
        }
        debouncedHandleFilterChange();
    };

    const onInputChange = (event: Event) => {
        searchQuery = (event.target as HTMLInputElement).value;
        isUserSearching = searchQuery !== '';
    };

    const clearInput = () => {
        if (inputElement) {
            inputElement.value = '';
            searchQuery = '';
            isUserSearching = false;
        }
    };

    const resetSelection = () => {
        selectedValue = [];
        selectedLabel = [];
        values = selectedValue;
        labels = selectedLabel;
        debouncedHandleFilterChange();
    };

    // remote validate the selection (e.g dialog is closed from outside click)
    export const validateSelection = () => {
        values = selectedValue;
        labels = selectedLabel;
        handleValidateSelection();
    };
</script>

<div class="flex h-full w-full flex-col">
    <div class="no-scrollbar relative flex flex-1">
        <div
            bind:this={scrollContainerRef}
            class="no-scrollbar flex-1 overflow-y-scroll"
        >
            {#if $areSecondaryFiltersActive || $arePillarFiltersActive}
                <div class="flex flex-col gap-m py-m">
                    <FilterBadges />
                </div>
            {/if}

            <!-- Alphabetical Scroll Bar -->
            {#if enableAlphabeticalGrouping && filteredOptions && filteredOptions.length > 0 && !isUserSearching}
                <div
                    class="no-scrollbar fixed right-0 top-0 z-[1] flex h-full flex-col items-center justify-center overflow-y-scroll pr-s"
                >
                    <div
                        class="my-auto flex h-[80vh] w-fit flex-col justify-center gap-2xs bg-white text-xs font-semibold text-content-quiet desktop:gap-3xs"
                    >
                        {#each allLetters as letter (letter)}
                            <button
                                type="button"
                                class={cn(
                                    'flex size-l items-center justify-center rounded-full transition-colors hover:bg-container-quiet hover:text-blue-brand',
                                    !availableLetters.includes(letter) &&
                                        'cursor-not-allowed text-content-quiet opacity-[0.38] hover:bg-none hover:text-content-neutral',
                                )}
                                disabled={!availableLetters.includes(letter)}
                                on:click={() =>
                                    availableLetters.includes(letter) &&
                                    scrollToLetter(letter)}
                            >
                                {letter}
                            </button>
                        {/each}
                    </div>
                </div>
            {/if}

            <!-- Input -->
            <div
                class="relative mb-m flex items-center border-b border-border-neutral"
            >
                <VpSearchIcon
                    class="pointer-events-none absolute size-l text-content-quiet"
                />
                <input
                    type="text"
                    on:input={onInputChange}
                    {placeholder}
                    bind:this={inputElement}
                />
                {#if isUserSearching}
                    <button
                        type="button"
                        class="absolute right-m mr-s"
                        on:click={clearInput}
                    >
                        <VpCloseIcon class="size-l text-content-quiet" />
                    </button>
                {/if}
            </div>

            {#if isUserSearching}
                <button
                    class="text-sm text-content-brand hover:underline"
                    on:click={() => {
                        selectedValue = filteredOptions.map(
                            (option) => option.value,
                        );
                        selectedLabel = filteredOptions.map(
                            (option) => option.label,
                        );
                    }}>{trans('search.filter.sidebar.select_all')}</button
                >
            {:else if allLabel}
                <CheckboxElement
                    labelClass="text-content-brand"
                    onToggleCheck={() => {
                        if (
                            selectedValue.length === 1 &&
                            selectedValue[0] === 'all'
                        ) {
                            selectedValue = [];
                            selectedLabel = [];
                            return;
                        }
                        selectedValue = ['all'];
                        selectedLabel = [allLabel];
                        values = selectedValue;
                        labels = selectedLabel;

                        debouncedHandleFilterChange();
                    }}
                    label={allLabel}
                    isChecked={selectedValue.length === 1 &&
                        selectedValue[0] === 'all'}
                />
            {/if}

            {#if !isUserSearching && isSuggestion}
                <div
                    class="my-s flex flex-col text-center text-sm text-content-quiet"
                >
                    {#if isSuggestion}
                        <span class="mb-s">
                            {trans('search.filter.sidebar.suggestions')}
                        </span>
                    {/if}
                    <hr class="h-px w-full bg-content-quiet" />
                </div>
            {/if}

            {#if filteredOptions && filteredOptions.length > 0}
                {#if enableAlphabeticalGrouping}
                    {#each availableLetters as letter (letter)}
                        <div id="letter-group-{letter}" class="mb-m">
                            <h3 class="py-xs text-l font-semibold">
                                {letter}
                            </h3>
                            {#each groupedOptions[letter] as filteredOption (filteredOption.value)}
                                <CheckboxElement
                                    onToggleCheck={() => {
                                        handleSelection(filteredOption);
                                    }}
                                    label={`${filteredOption.label} (${filteredOption.count})`}
                                    isChecked={selectedValue.includes(
                                        filteredOption.value,
                                    )}
                                    isDisabled={filteredOption.count === 0}
                                />
                            {/each}
                        </div>
                    {/each}
                {:else}
                    {#each filteredOptions as filteredOption (filteredOption.value)}
                        <CheckboxElement
                            onToggleCheck={() => {
                                handleSelection(filteredOption);
                            }}
                            label={`${filteredOption.label} (${filteredOption.count})`}
                            isChecked={selectedValue.includes(
                                filteredOption.value,
                            )}
                            isDisabled={filteredOption.count === 0}
                        />
                    {/each}
                {/if}
            {:else}
                <NoResultsFilter />
            {/if}
        </div>
    </div>

    <div
        class="sticky bottom-0 left-0 mt-auto flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
    >
        <Button
            size="small"
            intent="secondary"
            state={selectedValue.length === 0 ? 'disabled' : undefined}
            shape="rounded"
            type="button"
            disabled={selectedValue.length === 0}
            on:click={resetSelection}
            startIcon={VpReturnIcon}
        >
            {trans('search.filter.sidebar.clear')}
        </Button>
        <Button
            size="small"
            shape="rounded"
            type="button"
            on:click={() => validateSelection()}
            startIcon={VpCheckIcon}
            state={$filtersContext.isSubmitting ? 'loading' : undefined}
        >
            {trans('search.filter.sidebar.apply')}
        </Button>
    </div>
</div>

<style lang="postcss">
    input {
        appearance: unset !important;
        display: inline-block !important;
        margin: unset !important;
        padding: unset !important;
        max-width: unset !important;
        min-height: unset !important;
        outline: unset !important;
        background-color: inherit !important;
        font-family: inherit !important;
        font-size: inherit !important;
        color: inherit !important;
        border: unset !important;
        border-radius: unset !important;
        box-shadow: unset !important;
        transition: unset !important;
        background-position-x: 16px;
        @apply block w-full bg-white px-xl py-xs leading-6 placeholder:text-sm placeholder:text-content-quiet !important;
    }
</style>
